import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Settings as SettingsIcon, RefreshCw, Database, Building2, MapPin, Loader2, CheckCircle2, Languages, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/pages/Residents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Settings() {
  const { profile } = useOutletContext();
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const loadStatus = async () => {
    try { const r = await api.get("/regions/cache-status"); setStatus(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { loadStatus(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/regions/sync");
      toast.success(r.data.message);
      loadStatus();
    } catch (e) { toast.error(apiError(e)); } finally { setSyncing(false); }
  };
  const LEVELS = [
    { key: "provinces", label: "Provinsi" },
    { key: "regencies", label: "Kabupaten/Kota" },
    { key: "districts", label: "Kecamatan" },
    { key: "villages", label: "Desa/Kelurahan" },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <PageHeader icon={SettingsIcon} title="Pengaturan Wilayah" subtitle="Sinkronisasi data wilayah & informasi desa" />

      {/* Profile card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="settings-profile-card">
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Building2 size={20} className="text-[#064E3B]" /> Profil Desa</h2>
        <div className="flex items-start gap-4">
          {profile?.regency_logo_url ? (
            <img src={profile.regency_logo_url} alt="logo" className="h-20 w-20 object-contain" />
          ) : (
            <div className="h-20 w-20 rounded-lg bg-slate-100 grid place-items-center"><Building2 className="text-slate-400" /></div>
          )}
          <div className="space-y-1">
            <div className="font-heading text-xl font-bold text-[#064E3B]">Desa {profile?.village_name}</div>
            <div className="text-sm text-slate-600 flex items-center gap-1"><MapPin size={14} /> Kec. {profile?.district_name}, {profile?.regency_name}</div>
            <div className="text-sm text-slate-500">{profile?.province_name}</div>
            {profile?.village_head_name && <div className="text-sm text-slate-500">Kepala Desa: {profile.village_head_name}</div>}
          </div>
        </div>
      </div>

      {/* Sync card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="settings-sync-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2"><Database size={20} className="text-[#064E3B]" /> Sinkronisasi Data Wilayah</h2>
            <p className="text-sm text-slate-500 mt-1 max-w-md">
              Data wilayah diambil dari <b>wilayah.id</b> dan disimpan di database lokal. Klik tombol di bawah untuk memperbarui daftar provinsi.
            </p>
          </div>
          <Button onClick={sync} disabled={syncing} data-testid="region-sync-button" className="bg-[#D97706] hover:bg-[#B45309] rounded-xl h-11">
            {syncing ? <Loader2 className="animate-spin mr-2" size={18} /> : <RefreshCw size={18} className="mr-2" />}
            Sinkronisasi Sekarang
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          {LEVELS.map((l) => (
            <div key={l.key} className="rounded-xl bg-slate-50 border border-slate-100 p-4" data-testid={`cache-${l.key}`}>
              <div className="font-heading text-2xl font-extrabold text-[#064E3B]">{status ? status[l.key] : "—"}</div>
              <div className="text-xs text-slate-500 mt-0.5">{l.label} tersimpan</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-4 text-xs text-slate-400">
          <CheckCircle2 size={14} className="text-emerald-500" />
          Data kecamatan & desa disimpan otomatis (cache) saat pertama kali dibuka pada setup.
        </div>
      </div>

      {/* Sebutan wilayah */}
      <TermsCard />
    </div>
  );
}

function TermsCard() {
  const FIELDS = [
    { key: "village_term", label: "Sebutan Desa", hint: "cth: Desa, Kelurahan, Nagari, Gampong" },
    { key: "head_term", label: "Sebutan Kepala", hint: "cth: Kepala Desa, Lurah, Wali Nagari, Keuchik" },
    { key: "district_term", label: "Sebutan Kecamatan", hint: "cth: Kecamatan, Distrik, Kapanewon" },
    { key: "dusun_term", label: "Sebutan Dusun", hint: "cth: Dusun, Jorong, Lingkungan" },
  ];
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/region-terms");
      setData(r.data);
      setForm(r.data.effective || {});
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/region-terms", { ...form, override: true });
      setData(r.data); setForm(r.data.effective || {});
      toast.success("Sebutan wilayah disimpan");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const r = await api.put("/region-terms", { override: false });
      setData(r.data); setForm(r.data.effective || {});
      toast.success("Dikembalikan ke deteksi otomatis");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="settings-terms-card">
      <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2"><Languages size={20} className="text-[#064E3B]" /> Sebutan Wilayah</h2>
      <p className="text-sm text-slate-500 mt-1 max-w-2xl">
        Sistem <b>otomatis</b> memilih sebutan yang tepat untuk daerah Anda (mis. Nagari/Wali Nagari, Gampong/Keuchik, Kampung/Distrik) berdasarkan wilayah yang dipilih.
        Anda bisa mengubahnya manual bila berbeda. Sebutan ini dipakai pada kop & isi surat.
      </p>
      {data && (
        <div className="mt-3 inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700" data-testid="terms-status">
          <CheckCircle2 size={13} /> {data.is_override ? "Mode manual (override aktif)" : "Terdeteksi otomatis"}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        {FIELDS.map((f) => (
          <div key={f.key} className="space-y-1.5">
            <Label className="text-sm">{f.label}</Label>
            <Input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className="h-11" data-testid={`terms-${f.key}`} />
            <p className="text-xs text-slate-400">{f.hint}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mt-5">
        <Button onClick={save} disabled={saving} data-testid="terms-save" className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl h-11">
          {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Simpan Sebutan
        </Button>
        <Button onClick={reset} disabled={saving} variant="outline" data-testid="terms-reset" className="rounded-xl h-11 border-slate-300">
          <RotateCcw size={16} className="mr-1.5" /> Otomatis
        </Button>
      </div>
    </div>
  );
}
